import { Dialog, Button, DialogContent, DialogTitle } from "@mui/material";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTheme } from "../../../../components/hooks/useTheme";
import colorConfigs from "../../../../configs/colorConfigs";
import moment from "moment";
import useGeneral from "../../../../components/hooks/useGeneral";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import baseURL from "../../../../configs/api";
import useConfirm from "../../../../components/hooks/useConfirm";

type Props = {
    editPopup: boolean;
    hidePopup: () => void;
    particularEmailDetails: any;
}

export default function EmailSentDetails({ editPopup, hidePopup, particularEmailDetails }: Props) {
    const { themeStyles } = useTheme();
    const { dateFormat }: any = useGeneral();
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [emailDetails, setEmailDetails] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [previewPopup, setPreviewPopup] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [isLoadingThePdf, setIsLoadingPdf] = useState<boolean>(false);

    useEffect(() => {
        if (editPopup && particularEmailDetails?.realId) {
            fetchEmailDetails(particularEmailDetails.realId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editPopup, particularEmailDetails]);

    const fetchEmailDetails = (id: string) => {
        setLoading(true);

        fetch(`${baseURL}emailLogs/GetEmailDetail/${id}`, {
            headers: {
                'X-Tenant-ID': tenantId,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setEmailDetails(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Email detail error:", err);
                setLoading(false);
            });
    };

    const openResendConfirm = (id: number) => {
        setConfirmData({
            title: "Resend Email?",
            summary: "Are you sure you want to resend this email?",
            confirmBtn: "Yes",
            isConfirm: (callback: any) => {
                callback();
                handleResend(id);
            }
        });

        setConfirmPopup(true);
    };

    const handleResend = async (id: number) => {
        try {
            setLoading(true);

            await fetch(`${baseURL}resend/${id}`, {
                method: "POST",
                headers: {
                    "X-Tenant-ID": tenantId,
                },
            });

        } catch (err) {
            console.error("Resend error:", err);

        } finally {
            setLoading(false);
        }
    };

    const openEmailBodyInNewTab = () => {
        if (!emailDetails?.htmlBody || !emailDetails?.id) return;

        const url = `/admin/logs/emailssent/view/${emailDetails.id}`;
        window.open(url, "_blank");
    };

    const getStatusBadge = (status: string) => {
        const value = (status || "").toLowerCase();

        let colorBg = "bg-gray-200";
        let colorText = "text-gray-600";

        switch (value) {
            case "processed":
            case "queue":
            case "queued":
                colorBg = "bg-[#E2E8F0]";
                colorText = "text-[#475569]";
                break;

            case "delivered":
                colorBg = "bg-[#D9F4D4]";
                colorText = "text-[#039855]";
                break;

            case "deferred":
                colorBg = "bg-[#FFF4CC]";
                colorText = "text-[#B45309]";
                break;

            case "bounce":
            case "bounced":
                colorBg = "bg-[#FEDFDD]";
                colorText = "text-[#D92D20]";
                break;

            case "blocked":
            case "dropped":
                colorBg = "bg-[#FBEAEA]";
                colorText = "text-[#B91C1C]";
                break;

            case "spamreport":
                colorBg = "bg-[#FFE4E6]";
                colorText = "text-[#BE123C]";
                break;

            case "unsubscribe":
                colorBg = "bg-[#E9D5FF]";
                colorText = "text-[#6D28D9]";
                break;

            case "open":
            case "opened":
                colorBg = "bg-[#FEF9C3]";
                colorText = "text-[#92400E]";
                break;
        }

        return (
            <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${colorBg} ${colorText}`}>
                {status}
            </span>
        );
    };

    const closePreview = () => {
        setPreviewPopup(false);
        setIsPdf(false);
        setIsLoadingPdf(false);
        setIsFullScreen(false);
        setPreviewUrl("");
    };

    return (
        <>
            <Dialog
                open={editPopup}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '800px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[24px] py-[10px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`sticky top-0 z-10 flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Email Details</h2>
                                    </div>

                                    <button
                                        onClick={hidePopup}
                                        className="absolute right-2 top-2 text-gray-500 hover:text-red-500 transition"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">close</span>
                                    </button>
                                </div>

                                <div className="mt-2 p-2">
                                    {loading ? (
                                        <div className="w-full py-10 flex justify-center items-center">
                                            <span className="text-sm text-gray-500">Loading...</span>
                                        </div>
                                    ) : (
                                        <div className="w-full">
                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Subject</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.subject}</span>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>To Name</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.recipientName}</span>
                                                    </div>

                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>From Name</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.senderName}</span>
                                                    </div>

                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>CC</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.ccEmails}</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>To Email</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.recipientEmail}</span>
                                                    </div>

                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>From Email</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.senderEmail}</span>
                                                    </div>

                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>BCC</span>
                                                        <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{emailDetails?.bccEmails}</span>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col gap-1.5 col-span-2">
                                                    <button
                                                        onClick={openEmailBodyInNewTab}
                                                        className="text-xs font-medium underline cursor-pointer w-max"
                                                        style={{ color: "var(--app-link-color)" }}
                                                    >
                                                        View Email Body
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1.5 mt-5">
                                                <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Delivery Details</h2>
                                            </div>
                                            <hr />

                                            <div className="mt-4">
                                                <table className="w-full border border-gray-200 text-sm">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="p-2 border">#</th>
                                                            <th className="p-2 border">Recipient</th>
                                                            <th className="p-2 border">Email Status</th>
                                                            <th className="p-2 border">Change At</th>
                                                            <th className="p-2 border">Resend</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {emailDetails?.emailEventLogDtos?.map((row: any, index: number) => (
                                                            <tr key={row.id} className="text-center">
                                                                <td className={`text-sm p-2 border ${themeStyles.main.textSubHeadingColor}`}>{row.id}</td>

                                                                <td className={`text-sm p-2 border ${themeStyles.main.textSubHeadingColor}`}>{row.email}</td>

                                                                <td className="p-2 border capitalize">
                                                                    {getStatusBadge(row.eventType)}
                                                                </td>

                                                                <td className={`text-sm p-2 border ${themeStyles.main.textSubHeadingColor}`}>
                                                                    {row.timestamp ? moment(row.timestamp).format(dateFormat + " hh:mm a") : ""}
                                                                </td>

                                                                <td className="p-2 border">
                                                                    <button
                                                                        className={`text-xs px-3 py-1 rounded-md ${colorConfigs.btn.main.tailwind} border ${themeStyles.main.borderColor} text-white transition`}
                                                                        onClick={() => openResendConfirm(emailDetails?.id)}
                                                                    >
                                                                        Resend
                                                                    </button>
                                                                </td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="flex items-center gap-1.5 mt-5">
                                                <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Attachments</h2>
                                            </div>
                                            <hr />

                                            <div className="grid grid-cols-2 gap-6 mt-4">
                                                <div className="space-y-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>
                                                            Filename
                                                        </span>

                                                        {!emailDetails?.attachmentPath ? (
                                                            <span className="text-sm text-gray-500">
                                                                No attachment available
                                                            </span>
                                                        ) : (
                                                            <button
                                                                onClick={() => {
                                                                    const url = emailDetails?.attachmentPath || "";

                                                                    const pdf = url.toLowerCase().endsWith(".pdf");

                                                                    setPreviewUrl(url);
                                                                    setIsPdf(pdf);
                                                                    setIsLoadingPdf(pdf);
                                                                    setPreviewPopup(true);
                                                                }}
                                                                className="text-sm text-blue-600 underline cursor-pointer text-left hover:text-blue-800"
                                                            >
                                                                {emailDetails?.attachmentPath?.split("name=")[1] || "Open File"}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-start gap-4 p-4 z-10">
                                                <Button
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={hidePopup}
                                                >
                                                    Close
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={previewPopup}
                onClose={(event, reason) => {
                    if (reason === "backdropClick") return;
                    setPreviewPopup(false);
                }}
                maxWidth="md"
                fullScreen={isFullScreen}
                PaperProps={{
                    style: {
                        width: isFullScreen ? "" : "35%",
                        marginTop: isFullScreen ? "" : "61px",
                    },
                }}
            >
                <DialogTitle
                    sx={{ padding: "0px", width: "100%" }}
                    className={`${themeStyles.main.tailwindSectionBg}`}
                >
                    <div
                        style={{
                            height: "50px",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 16px 8px 16px",
                        }}
                    >
                        <p style={{ fontSize: "14px", marginLeft: "5px" }}
                            className={`${themeStyles.main.textHeadingColor}`}>
                            Preview
                        </p>
                        <CloseRoundedIcon
                            className={`text-xl mr-4 cursor-pointer ${themeStyles.main.textHeadingColor}`}
                            onClick={closePreview}
                        />
                    </div>
                    <hr style={{ marginBlock: "0px" }} />
                    <hr style={{ marginBlock: "0px" }} />
                </DialogTitle>

                <DialogContent sx={{ padding: "0px" }} className={`${themeStyles.main.tailwindSectionBg}`}>
                    <>
                        {isPdf ? (
                            isLoadingThePdf ? (
                                <div
                                    style={{
                                        width: "100%",
                                        height: isFullScreen ? "calc(100% - 60px)" : "400px",
                                    }}
                                    className={`${themeStyles.main.textSubHeadingColor} flex items-center justify-center`}
                                >
                                    Loading PDF...
                                </div>
                            ) : (
                                <embed
                                    src={previewUrl}
                                    type="application/pdf"
                                    style={{
                                        width: "100%",
                                        height: isFullScreen ? "calc(100% - 60px)" : "400px",
                                    }}
                                    onLoad={() => setIsLoadingPdf(false)}
                                />
                            )
                        ) : (
                            <img
                                src={previewUrl}
                                alt="attachment"
                                style={{
                                    width: "100%",
                                    height: isFullScreen ? "calc(100% - 60px)" : "300px",
                                    objectFit: "contain",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                            />
                        )}

                        <div
                            style={{
                                width: "100%",
                                height: "50px",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                paddingBlock: "8px",
                                paddingLeft: "16px",
                                paddingRight: "16px",
                            }}
                        >
                            <div style={{ width: "50%" }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#FFFFFF",
                                        color: "black",
                                        borderRadius: "0",
                                        border: "none",
                                        textTransform: "capitalize",
                                        fontSize: "12px",
                                        "&: hover": { backgroundColor: "#e0dee0" },
                                    }}
                                    onClick={() => setIsFullScreen(!isFullScreen)}
                                >
                                    {isFullScreen && "Exit "} Full Screen
                                </Button>
                            </div>
                        </div>
                    </>

                </DialogContent>
            </Dialog>
        </>
    )
}